import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Percent, Check, Info, Plus, Trash } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { getTvaRates, updateTvaRates } from '../services/api';
import { PageHero } from '../components/motion/PageHero';

// Liste exhaustive des territoires francais (ISO 3166-1 alpha-2)
const TERRITOIRES_META = [
  { code: 'FR', label: 'France métropolitaine', region: 'Europe' },
  { code: 'GP', label: 'Guadeloupe', region: 'DOM' },
  { code: 'MQ', label: 'Martinique', region: 'DOM' },
  { code: 'GF', label: 'Guyane', region: 'DOM' },
  { code: 'RE', label: 'La Réunion', region: 'DOM' },
  { code: 'YT', label: 'Mayotte', region: 'DOM' },
  { code: 'PM', label: 'Saint-Pierre-et-Miquelon', region: 'COM' },
  { code: 'BL', label: 'Saint-Barthélemy', region: 'COM' },
  { code: 'MF', label: 'Saint-Martin', region: 'COM' },
  { code: 'NC', label: 'Nouvelle-Calédonie', region: 'COM Pacifique' },
  { code: 'PF', label: 'Polynésie française', region: 'COM Pacifique' },
  { code: 'WF', label: 'Wallis-et-Futuna', region: 'COM Pacifique' },
  { code: 'TF', label: 'TAAF', region: 'COM' },
];

export default function TvaSettingsPage() {
  const [rates, setRates] = useState({});
  const [fallback, setFallback] = useState(8.5);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [customRate, setCustomRate] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await getTvaRates();
      setRates(r.data.rates || {});
      setFallback(r.data.fallback_rate ?? 8.5);
    } catch {
      toast.error('Erreur chargement TVA');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setRate = (code, value) => {
    setRates((prev) => ({ ...prev, [code]: value }));
  };

  const removeRate = (code) => {
    setRates((prev) => {
      const n = { ...prev };
      delete n[code];
      return n;
    });
  };

  const addCustom = () => {
    if (!customCode || !/^[A-Z]{2}$/.test(customCode.toUpperCase())) {
      toast.error('Code territoire invalide (2 lettres ISO)');
      return;
    }
    const r = parseFloat(customRate);
    if (isNaN(r) || r < 0 || r > 30) {
      toast.error('Taux invalide (0-30 %)');
      return;
    }
    setRate(customCode.toUpperCase(), r);
    setCustomCode(''); setCustomRate('');
    toast.success('Territoire ajouté');
  };

  const save = async () => {
    setSaving(true);
    try {
      // Convert rates to numbers
      const cleanRates = {};
      Object.entries(rates).forEach(([k, v]) => {
        const n = parseFloat(v);
        if (!isNaN(n) && n >= 0 && n <= 30) cleanRates[k] = n;
      });
      await updateTvaRates({ rates: cleanRates, fallback_rate: parseFloat(fallback) });
      toast.success('Configuration TVA sauvegardée');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur sauvegarde');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin" /></div>;

  // Merge meta + custom rates
  const known = new Set(TERRITOIRES_META.map((t) => t.code));
  const customCodes = Object.keys(rates).filter((c) => !known.has(c));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tva-settings-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Paramètres TVA"
        subtitle="Configuration des taux, comptes et règles de facturation"
        icon={<Percent size={24} weight="duotone" className="text-white" />}
        gradient="from-[#FF8E00] to-[#D5007D]"
        accents={['#00AEEF', '#80C51F']}
      />

      <div>
        <h1 className="text-3xl font-bold text-[#1A2421] tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Paramètres TVA par région
        </h1>
        <p className="text-[#5E6D66] mt-1">Taux applicable selon le territoire du client (Contact/Organisation).</p>
      </div>

      <Card className="border-[#E8E6E1] bg-blue-50/50">
        <CardContent className="p-4 flex items-start gap-3">
          <Info size={18} className="text-blue-700 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-[#1A2421]">
            <p><strong>Comment ça marche ?</strong> À la génération d'une facture (après paiement Stripe), le taux TVA appliqué est celui du champ <span className="font-mono text-xs bg-white px-1 rounded">territoire</span> du client. Si le territoire est absent du mapping, le taux de repli est utilisé.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#E8E6E1]">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#1A2421] uppercase tracking-wider">Taux par territoire</h2>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Taux de repli</Label>
              <Input
                type="number"
                step="0.5"
                value={fallback}
                onChange={(e) => setFallback(e.target.value)}
                className="w-24 border-[#E8E6E1]"
                data-testid="fallback-rate-input"
              />
              <span className="text-sm">%</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {TERRITOIRES_META.map((t) => (
              <div key={t.code} className="flex items-center gap-2 p-2 border border-[#E8E6E1] rounded-md hover:bg-[#F9F8F5]" data-testid={`tva-row-${t.code}`}>
                <Badge variant="outline" className="text-[10px] font-mono">{t.code}</Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{t.label}</p>
                  <p className="text-[10px] text-[#5E6D66]">{t.region}</p>
                </div>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="30"
                  value={rates[t.code] ?? ''}
                  onChange={(e) => setRate(t.code, e.target.value)}
                  placeholder={`${fallback}`}
                  className="w-20 border-[#E8E6E1] text-right"
                  data-testid={`tva-input-${t.code}`}
                />
                <Percent size={14} className="text-[#5E6D66]" />
              </div>
            ))}
          </div>

          {customCodes.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-[#5E6D66] uppercase tracking-wider mb-2">Territoires personnalisés</p>
              <div className="space-y-1">
                {customCodes.map((code) => (
                  <div key={code} className="flex items-center gap-2 p-2 border border-purple-200 rounded-md bg-purple-50/50">
                    <Badge className="bg-purple-100 text-purple-800 text-[10px] font-mono">{code}</Badge>
                    <Input
                      type="number"
                      step="0.5"
                      value={rates[code]}
                      onChange={(e) => setRate(code, e.target.value)}
                      className="w-24 border-[#E8E6E1] text-right"
                    />
                    <Percent size={14} className="text-[#5E6D66]" />
                    <Button size="icon" variant="ghost" onClick={() => removeRate(code)} className="text-red-600 ml-auto">
                      <Trash size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-[#E8E6E1] pt-3">
            <p className="text-xs font-semibold text-[#5E6D66] uppercase tracking-wider mb-2">Ajouter un territoire</p>
            <div className="flex gap-2 items-end">
              <div>
                <Label className="text-[10px]">Code ISO (2 lettres)</Label>
                <Input value={customCode} onChange={(e) => setCustomCode(e.target.value.toUpperCase())} placeholder="ex: BE" maxLength={2} className="w-24 border-[#E8E6E1] font-mono" data-testid="custom-code-input" />
              </div>
              <div>
                <Label className="text-[10px]">Taux %</Label>
                <Input type="number" step="0.5" value={customRate} onChange={(e) => setCustomRate(e.target.value)} placeholder="21" className="w-24 border-[#E8E6E1]" data-testid="custom-rate-input" />
              </div>
              <Button variant="outline" onClick={addCustom} className="border-[#E8E6E1]" data-testid="add-custom-tva-btn">
                <Plus size={14} className="mr-1" /> Ajouter
              </Button>
            </div>
          </div>

          <div className="flex justify-end pt-3 border-t border-[#E8E6E1]">
            <Button onClick={save} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-tva-btn">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Check size={16} className="mr-2" />}
              Sauvegarder la configuration
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
